import { FIELD_NAME_KEY } from "PFApp/constants/activity_form/activity_form_field_keys";
import { RoleFormKey } from "PFApp/types/activity_form/activity_form_schema";
import { useCellInputFocus } from "PFComponents/table";
import { DecimalNumberInput } from "PFComponents/text/decimal_number_input";
import { memo, useCallback, useEffect, useRef } from "react";
import { FieldValues, UseFormGetValues, UseFormSubscribe } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { MAX_HOURS_PER_WEEK } from "./utils";

type AvailabilityCellProps = {
  rowIndex: number;
  colIndex: number;
  value: number | null;
  roleFormKey: RoleFormKey;
  weekRange: string;
  getValues: UseFormGetValues<FieldValues>;
  subscribe: UseFormSubscribe<FieldValues>;
  onCellChange: (roleFormKey: RoleFormKey, colIndex: number, value: number | undefined) => void;
  disabled?: boolean;
  error?: string;
  inputClassName?: string;
};

const AvailabilityCellComponent = ({
  rowIndex,
  colIndex,
  value,
  roleFormKey,
  weekRange,
  getValues,
  subscribe,
  onCellChange,
  disabled,
  error,
  inputClassName
}: AvailabilityCellProps) => {
  const { t } = useTranslation("activities");
  const ref = useRef<HTMLInputElement>(null);
  const { inputFocusProps } = useCellInputFocus(ref, { rowIndex, colIndex });
  const handleValueChange = useCallback(
    (newValue: number | undefined) => onCellChange(roleFormKey, colIndex, newValue),
    [onCellChange, roleFormKey, colIndex]
  );
  const roleNameFieldName = `${roleFormKey}.${FIELD_NAME_KEY}`;
  const getAriaLabel = useCallback(
    () =>
      t("engagementBlueprint.roleAvailabilityTitle", { roleName: getValues(roleNameFieldName), weekRange }),
    [t, getValues, roleNameFieldName, weekRange]
  );

  const syncLabelAttributes = useCallback(() => {
    const label = getAriaLabel();
    ref.current?.setAttribute("aria-label", label);
    ref.current?.setAttribute("title", label);
  }, [getAriaLabel]);

  useEffect(
    () =>
      subscribe({
        name: roleNameFieldName,
        exact: true,
        formState: { values: true },
        callback: syncLabelAttributes
      }),
    [subscribe, roleNameFieldName, syncLabelAttributes]
  );

  return (
    <DecimalNumberInput
      ref={ref}
      variant="table"
      controlledValue={value ?? undefined}
      onChange={handleValueChange}
      max={MAX_HOURS_PER_WEEK}
      disabled={disabled}
      error={error}
      inputClassName={inputClassName}
      aria-label={getAriaLabel()}
      {...inputFocusProps}
    />
  );
};

export const AvailabilityCell = memo(AvailabilityCellComponent);
